import { useEffect } from "react";

export const normalizeQuoteEventDetail = (detail) => {
  if (!detail) return [];

  if (Array.isArray(detail)) {
    return detail.filter(Boolean);
  }

  if (Array.isArray(detail.quotes)) {
    const fallbackOrderId = detail.orderId ?? null;
    return detail.quotes
      .filter(Boolean)
      .map((quote) =>
        fallbackOrderId && !quote.orderId ? { ...quote, orderId: fallbackOrderId } : quote
      );
  }

  if (detail.orderQuotesId) {
    return [detail];
  }

  return [];
};

export const groupQuotesByOrderId = (quotes = []) =>
  quotes.reduce((acc, quote) => {
    const orderId = quote?.orderId;
    if (!orderId) return acc;
    if (!acc[orderId]) acc[orderId] = [];
    acc[orderId].push(quote);
    return acc;
  }, {});

export const mergeQuotesIntoOrders = (prevOrders, groupedQuotes = {}) => {
  if (!Array.isArray(prevOrders) || !Object.keys(groupedQuotes).length) return prevOrders;

  let hasChanges = false;

  const updatedOrders = prevOrders.map((order) => {
    const incoming = groupedQuotes[order.orderId];
    if (!incoming || !incoming.length) return order;

    const currentQuotes = Array.isArray(order.orderQuotes) ? [...order.orderQuotes] : [];
    let orderChanged = false;

    incoming.forEach((incomingQuote) => {
      const idx = currentQuotes.findIndex(
        (quote) => quote.orderQuotesId === incomingQuote.orderQuotesId
      );

      if (idx === -1) {
        currentQuotes.push(incomingQuote);
        orderChanged = true;
        return;
      }

      const existingQuote = currentQuotes[idx];
      const hasDiff = Object.keys(incomingQuote).some(
        (key) => existingQuote[key] !== incomingQuote[key]
      );

      if (hasDiff) {
        currentQuotes[idx] = { ...existingQuote, ...incomingQuote };
        orderChanged = true;
      }
    });

    if (!orderChanged) return order;

    hasChanges = true;
    return { ...order, orderQuotes: currentQuotes };
  });

  return hasChanges ? updatedOrders : prevOrders;
};

export const useWebSocketQuoteUpdates = (setOrderData) => {
  useEffect(() => {
    if (typeof setOrderData !== "function") return;

    const handleOrderQuotesUpdate = (event) => {
      const normalizedQuotes = normalizeQuoteEventDetail(event.detail);
      if (!normalizedQuotes.length) return;

      const groupedQuotes = groupQuotesByOrderId(normalizedQuotes);
      if (!Object.keys(groupedQuotes).length) return;

      setOrderData((prevOrders) => mergeQuotesIntoOrders(prevOrders, groupedQuotes));
    };

    window.addEventListener("orderQuotesUpdate", handleOrderQuotesUpdate);
    return () => {
      window.removeEventListener("orderQuotesUpdate", handleOrderQuotesUpdate);
    };
  }, [setOrderData]);
};
